import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient, getHttpStatus } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import { EntityPatch } from '@evidentid/rpweb-api-client';
import {
    AddEntitiesFailure,
    EntityPostResponse,
    EntityUpsertResponse,
    EntityPatchResult,
    UpsertEntitiesFailure,
} from '@evidentid/rpweb-api-client/models';
import { Entity, EntityInput, EntityInputApiModel } from '@evidentid/tprm-portal-lib/models/dashboard';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { getDashboardActions } from '@/modules/dashboard/vuex/dashboard';
import { convertEntityInputViewToData } from '@/modules/entity-management/utils/entityProcessing';

function mergeBatchResults<T extends EntityPostResponse | EntityUpsertResponse>(
    partialResults: PromiseSettledResult<T>[]): T {
    return partialResults.reduce((result, partialResult) => (partialResult.status === 'fulfilled'
        ? {
            ...result,
            totalCount: result.totalCount + (partialResult.value.totalCount || 0),
            successCount: result.successCount + (partialResult.value.successCount || 0),
            failureCount: result.failureCount + (partialResult.value.failureCount || 0),
            successes: [ ...result.successes, ...(partialResult.value.successes || []) ],
            failures: [ ...result.failures, ...(partialResult.value.failures || []) ],
        }
        : result),
    {
        totalCount: 0,
        successCount: 0,
        failureCount: 0,
        successes: [] as Entity[],
        failures: [] as T['failures'],
    } as T);
}

/** How many of the succeeded entities were updates rather than creations, or `null` when the batch
 * cannot be split.
 *
 * The response says an entity was written but not which of the two it was, so it is read off the
 * request instead: an element carrying an `id` addressed an entity that already existed, and an
 * explicit `id: null` asked for a new one.
 *
 * An element with no `id` property at all asked the API to match it on `contactEmail`, which either
 * updates the entity holding that email or creates one - and nothing in the request or the response
 * says which happened. That is unknowable rather than zero, so the whole batch reports `null` and
 * the caller stops claiming a split it cannot compute (PRODUCT-26313).
 */
export function countUpdated(sentEntities: EntityInputApiModel[], successes: Entity[]): number | null {
    if (sentEntities.some((entity) => !('id' in entity))) {
        return null;
    }
    const sentIds = new Set(sentEntities.map((entity) => entity.id).filter(Boolean));
    return successes.filter((entity) => sentIds.has(entity.id)).length;
}

/** Whether the API rejected a whole chunk with HTTP 400, as opposed to refusing individual
 * elements of a request it accepted.
 */
function hasInvalidPayloadRejection(batchResults: PromiseSettledResult<unknown>[]): boolean {
    return batchResults.some((result) =>
        result.status === 'rejected' && getHttpStatus(result.reason) === 400);
}

export interface EntityManagementRequirement {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): EntityManagementRequirement {
    return store.$app;
}

// The legacy module fired these through app.observer.onMutation() hooks in
// its module-builder registration; inlined at the same transition points.
function trackEntitiesEvent(
    app: EntityManagementRequirement,
    event: { name: string, phase: string, subject: string, count?: number, countKey?: string },
    rpName: string,
): void {
    const { name, phase, subject, count, countKey } = event;
    const message = `[${phase}] ${subject} for ${rpName}`;
    app.tracker?.track({
        name: phase === 'Failed' ? `${name} Failed` : name,
        description: message,
        rpName,
        ...(countKey != null && count != null ? { [countKey]: count } : {}),
    });
    app.logger?.addBreadcrumb({
        ...(phase === 'Failed' ? { level: Severity.error } : {}),
        category: 'Dashboard - Entities',
        message,
        data: count != null ? { rpName, count } : { rpName },
    });
}

// totalCount = total entities requested for the batch call
// successOnRequestCount = successCount + failureCount(request succeed but entity not created/updated)
export interface BatchStatus {
    status: OperationStatus;
    progress: number;
    totalCount: number;
    successCount: number; // on success calls
    failureCount: number; // on success calls
    successOnRequestCount: number;
    failedOnRequestCount: number; // on failed calls
    successes: any[];
    failures: any[];
}

export interface BatchAddEntitiesStatus extends BatchStatus {
    successes: Entity[];
    failures: AddEntitiesFailure[];
}

export interface BatchUpsertEntitiesStatus extends BatchStatus {
    successes: Entity[];
    failures: UpsertEntitiesFailure[];
    // How many of `successCount` addressed an existing entity; the rest were created.
    updatedCount: number | null;
    // The API rejected the request itself (HTTP 400) rather than individual elements, so
    // retrying the same file cannot help.
    invalidPayload: boolean;
}

export interface PatchEntitiesStatus {
    status: OperationStatus;
    successCount: number;
    failureCount: number;
    totalCount: number;
    successes: EntityPatchResult[];
    failures: { element: EntityPatchResult, error: { message: string, error: string } } [];
    currentlyUpdated: string[];
    isBulk: boolean;
}

/** What consumers read before any upsert has run for the relying party. Shared because a
 * component's own fallback literal is never contextually typed — the store state it falls back
 * from is `any` — so a field added to the interface would silently go missing from it.
 */
export function createEmptyUpsertEntitiesStatus(): BatchUpsertEntitiesStatus {
    return {
        status: OperationStatus.uninitialized,
        progress: 0,
        totalCount: 0,
        successCount: 0,
        failureCount: 0,
        successOnRequestCount: 0,
        failedOnRequestCount: 0,
        successes: [],
        failures: [],
        updatedCount: 0,
        invalidPayload: false,
    };
}

interface EntityManagementState {
    addEntitiesStatus: Record<string, BatchAddEntitiesStatus>;
    upsertEntitiesStatus: Record<string, BatchUpsertEntitiesStatus>;
    patchEntitiesStatus: Record<string, PatchEntitiesStatus>;
}

export const useEntityManagementStore = defineStore('entityManagement', {
    state: (): EntityManagementState => ({
        addEntitiesStatus: {},
        upsertEntitiesStatus: {},
        patchEntitiesStatus: {},
    }),
    actions: {

        startAddingEntities(payload: { rpName: string, count: number }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Add Entities', phase: 'Requested', subject: 'Add entities', count: payload.count, countKey: 'addEntitiesCount' },
                payload.rpName);
            this.addEntitiesStatus = {
                ...this.addEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    progress: 0,
                    totalCount: payload.count,
                    successCount: 0,
                    failureCount: 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: 0,
                    failures: [],
                    successes: [],
                },
            };
        },
        failAddingEntities(payload: { rpName: string }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Add Entities', phase: 'Failed', subject: 'Add entities' },
                payload.rpName);
            this.addEntitiesStatus = {
                ...this.addEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    progress: 0,
                    totalCount: this.addEntitiesStatus[payload.rpName]?.totalCount || 0,
                    successCount: 0,
                    failureCount: this.addEntitiesStatus[payload.rpName]?.totalCount || 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: 0,
                    failures: [],
                    successes: [],
                },
            };
        },
        progressAddingEntities(payload: {
            rpName: string;
            progress: number;
        }) {
            this.addEntitiesStatus = {
                ...this.addEntitiesStatus,
                [payload.rpName]: {
                    ...this.addEntitiesStatus[payload.rpName],
                    progress: payload.progress,
                },
            };
        },
        finishAddingEntities(payload: {
            rpName: string;
            totalCount: number;
            successCount: number;
            failureCount: number;
            successOnRequestCount: number;
            failedOnRequestCount: number;
            successes: Entity[];
            failures: AddEntitiesFailure[];
        }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Add Entities', phase: 'Finished', subject: 'Add entities' },
                payload.rpName);
            this.addEntitiesStatus = {
                ...this.addEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    progress: 100,
                    totalCount: payload.totalCount,
                    successCount: payload.successCount,
                    failureCount: payload.failureCount,
                    successOnRequestCount: payload.successOnRequestCount,
                    failedOnRequestCount: payload.failedOnRequestCount,
                    failures: payload.failures,
                    successes: payload.successes,
                },
            };
        },
        startUpdateEntities(payload: { rpName: string, count: number }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Update Entities', phase: 'Requested', subject: 'Update entities', count: payload.count, countKey: 'updateEntitiesCount' },
                payload.rpName);
            this.upsertEntitiesStatus = {
                ...this.upsertEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    progress: 0,
                    totalCount: payload.count,
                    successCount: 0,
                    failureCount: 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: 0,
                    successes: [],
                    failures: [],
                    updatedCount: 0,
                    invalidPayload: false,
                },
            };
        },
        failUpdateEntities(payload: { rpName: string, invalidPayload?: boolean }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Update Entities', phase: 'Failed', subject: 'Update entities' },
                payload.rpName);
            this.upsertEntitiesStatus = {
                ...this.upsertEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    progress: 0,
                    totalCount: this.upsertEntitiesStatus[payload.rpName]?.totalCount || 0,
                    successCount: 0,
                    failureCount: 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: this.upsertEntitiesStatus[payload.rpName]?.totalCount || 0,
                    successes: [],
                    failures: [],
                    updatedCount: 0,
                    invalidPayload: Boolean(payload.invalidPayload),
                },
            };
        },
        progressUpdateEntities(payload: {
            rpName: string;
            progress: number;
        }) {
            this.upsertEntitiesStatus = {
                ...this.upsertEntitiesStatus,
                [payload.rpName]: {
                    ...this.upsertEntitiesStatus[payload.rpName],
                    progress: payload.progress,
                },
            };
        },
        finishUpdateEntities(payload: {
            rpName: string;
            totalCount: number;
            successCount: number;
            failureCount: number;
            successOnRequestCount: number;
            failedOnRequestCount: number;
            successes: Entity[];
            failures: UpsertEntitiesFailure[];
            updatedCount: number | null;
        }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Update Entities', phase: 'Finished', subject: 'Update entities' },
                payload.rpName);
            this.upsertEntitiesStatus = {
                ...this.upsertEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    progress: 100,
                    totalCount: payload.totalCount,
                    successCount: payload.successCount,
                    failureCount: payload.failureCount,
                    failedOnRequestCount: payload.failedOnRequestCount,
                    successOnRequestCount: payload.successOnRequestCount,
                    successes: payload.successes,
                    failures: payload.failures,
                    updatedCount: payload.updatedCount,
                    invalidPayload: false,
                },
            };
        },
        startPatchEntities(payload: { rpName: string, entitiesUpdate: EntityPatch[], isBulk: boolean }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Patch Entities', phase: 'Requested', subject: 'Patch entities' },
                payload.rpName);
            this.patchEntitiesStatus = {
                ...this.patchEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    successCount: 0,
                    failureCount: 0,
                    totalCount: payload.entitiesUpdate.length,
                    successes: [],
                    failures: [],
                    currentlyUpdated: payload.entitiesUpdate.map((patch) => patch.id),
                    isBulk: payload.isBulk,
                },
            };
        },
        failPatchEntities(payload: {
            rpName: string;
            successCount: number;
            failureCount: number;
            totalCount: number;
            successes: EntityPatchResult[];
            failures: { element: EntityPatchResult, error: { message: string, error: string } }[];
        }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Patch Entities', phase: 'Failed', subject: 'Patch entities' },
                payload.rpName);
            this.patchEntitiesStatus = {
                ...this.patchEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    successCount: payload.successCount,
                    failureCount: payload.failureCount,
                    totalCount: payload.totalCount,
                    successes: payload.successes,
                    failures: payload.failures,
                    currentlyUpdated: [],
                    isBulk: false,
                },
            };
        },
        finishPatchEntities(payload: {
            rpName: string;
            successCount: number;
            failureCount: number;
            totalCount: number;
            successes: EntityPatchResult[];
            failures: { element: EntityPatchResult, error: { message: string, error: string } }[];
        }) {
            trackEntitiesEvent(this.$app as EntityManagementRequirement,
                { name: 'Patch Entities', phase: 'Finished', subject: 'Patch entities' },
                payload.rpName);
            this.patchEntitiesStatus = {
                ...this.patchEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    successCount: payload.successCount,
                    failureCount: payload.failureCount,
                    totalCount: payload.totalCount,
                    successes: payload.successes,
                    failures: payload.failures,
                    currentlyUpdated: [],
                    isBulk: false,
                },
            };
        },

        async addEntities(payload: {
            rpName: string; entities: EntityInput[];
        }) {
            const { rpName } = payload;
            const entities = payload.entities.map(convertEntityInputViewToData);
            try {
                this.startAddingEntities({ rpName, count: entities.length });
                const updateProgress = (progress: number) => {
                    this.progressAddingEntities({ rpName, progress });
                };
                const batchResults = await appOf(this).gatewayClient.tprmUi()
                    .batchCreateTprmEntities(rpName, entities, 50, updateProgress);
                const results = mergeBatchResults(batchResults);
                const successOnRequestCount = results.totalCount;
                const failedOnRequestCount = entities.length - successOnRequestCount;
                if (entities.length > 0 && failedOnRequestCount === entities.length) {
                    this.failAddingEntities({ rpName });
                } else {
                    this.finishAddingEntities({
                        rpName,
                        ...results,
                        totalCount: entities?.length || 0,
                        successOnRequestCount,
                        failedOnRequestCount,
                    });
                }
            } catch (error) {
                console.warn('Add entities error', error);
                this.failAddingEntities({ rpName });
            }
        },
        /** `strictProducerChecks` asks the API to hold changed producer lists to the portal's rules against
         * each entity's coverage types. Bulk import does; entity details checks its own form. */
        async updateEntities(payload: {
            rpName: string;
            entities: EntityInput[];
            updateEntitiesListState?: boolean;
            strictProducerChecks?: boolean;
        }) {
            const { rpName, strictProducerChecks = false } = payload;
            const entities = payload.entities.map(convertEntityInputViewToData);
            try {
                this.startUpdateEntities({ rpName, count: entities.length });
                const updateProgress = (progress: number) => {
                    this.progressUpdateEntities({ rpName, progress });
                };
                const batchResults = await appOf(this).gatewayClient.tprmUi()
                    .batchUpsertTprmEntities(rpName, entities, 50, { updateProgress, strictProducerChecks });
                const results = mergeBatchResults(batchResults);
                const successOnRequestCount = results.totalCount;
                const failedOnRequestCount = (entities?.length - results.totalCount) || 0;
                if (entities.length > 0 && failedOnRequestCount === entities.length) {
                    this.failUpdateEntities({
                        rpName,
                        invalidPayload: hasInvalidPayloadRejection(batchResults),
                    });
                } else {
                    this.finishUpdateEntities({
                        rpName,
                        ...results,
                        totalCount: entities?.length || 0,
                        successOnRequestCount,
                        failedOnRequestCount,
                        updatedCount: countUpdated(entities, results.successes),
                    });
                    if (payload.updateEntitiesListState) {
                        await getDashboardActions()
                            .updateEntitiesInList({ rpName, entities: results.successes });
                    }
                }
            } catch (error) {
                console.warn('Update entities error', error);
                this.failUpdateEntities({ rpName });
            }
        },
        /**
         * Producer emails the API would refuse for using a personal or disposable domain.
         *
         * Returns an empty list when the check itself fails: this only exists to flag the problem
         * while the user is still editing, and the entity upsert performs the same check regardless,
         * so a failure here must not invent an error or block a save that would have succeeded.
         */
        async validateProducerEmails(payload: { rpName: string, emails: string[] }): Promise<string[]> {
            const { rpName, emails } = payload;
            if (emails.length === 0) {
                return [];
            }
            try {
                return await appOf(this).gatewayClient.tprmUi().validateProducerEmails(rpName, emails);
            } catch (error) {
                console.warn('Validate producer emails error', error);
                return [];
            }
        },
        clearAddEntitiesStatus(payload: { rpName: string }) {
            this.addEntitiesStatus = omit(this.addEntitiesStatus, [ payload.rpName ]);
        },
        clearUpdateEntitiesStatus(payload: { rpName: string }) {
            this.upsertEntitiesStatus = omit(this.upsertEntitiesStatus, [ payload.rpName ]);
        },
        clearPatchEntitiesStatus(payload: { rpName: string }) {
            this.patchEntitiesStatus = omit(this.patchEntitiesStatus, [ payload.rpName ]);
        },
        async patchEntities(payload: {
            rpName: string;
            patch: EntityPatch[];
            isBulk: boolean;
        }) {
            const { rpName, patch, isBulk } = payload;
            try {
                this.startPatchEntities({ rpName, entitiesUpdate: patch, isBulk });
                const { successCount, failureCount, totalCount, successes, failures } =
                    await appOf(this).gatewayClient.tprmUi().patchTprmEntity(rpName, patch);
                if (totalCount === failureCount) {
                    this.failPatchEntities(
                        { rpName, successCount, failureCount, totalCount, successes, failures },
                    );
                } else {
                    this.finishPatchEntities({
                        rpName,
                        successCount,
                        failureCount,
                        totalCount,
                        successes,
                        failures,
                    });
                    await getDashboardActions().patchEntitiesInList(
                        { rpName: payload.rpName, entitiesPatch: successes });
                }
            } catch (error) {
                console.warn('Patch entities error', error);
                this.failPatchEntities({
                    rpName,
                    successCount: 0,
                    failureCount: patch.length,
                    totalCount: patch.length,
                    successes: [],
                    failures: [],
                });
            }
        },
    },
});
